import React, { ReactNode, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Size } from '../../common/size';
import { useTestId } from '../../hooks/useTestId';
import { Text } from '../../Typography/Text/Text';
import { IThemePalette } from '../../Themes/interfaces';

export const tabColours = (palette: IThemePalette) => ({
  tab: {
    // TODO: check if this is used outside web-design. Remove and use `selected` directly
    tab: {
      selected: {
        border: palette.primary.first[100]
      }
    },
    selected: {
      border: palette.primary.first[100]
    },
    header: {
      background: palette.neutral.white
    }
  }
});

export interface ITabItemHeaderProps {
  isActive: boolean;
}

const tabActiveBorderStyle = css`
  content: '';
  display: block;
  box-sizing: border-box;
  position: absolute;
  width: 100%;
  height: 0.25rem;
  margin: 0 auto;
  left: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.colours.tab.selected.border};
  border: transparent;
`;

const TabItemHeader = styled.div<ITabItemHeaderProps>`
  padding: 2rem 8rem;
  display: inline-block;
  position: relative;
  margin: 0;
  cursor: pointer;

  ${({ isActive }) =>
    isActive &&
    css`
      &:after {
        ${tabActiveBorderStyle}
      }
    `}
  &:hover:after {
    ${tabActiveBorderStyle}
  }
`;

const TabHeader = styled.div`
  display: flex;
  background-color: ${({ theme }) => theme.colours.tab.header.background};
  align-items: center;
`;

const PullRight = styled.div`
  margin-left: auto;
`;

const TabContainer = styled.div``;
const TabStripContainer = styled.div``;
const TabChildren = styled.div``;

export interface ITabStripProps {
  defaultActiveTabIndex?: number;
  renderRightChild?: ReactNode;
}

export interface ITabProps {
  title: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

export const Tab: React.FC<ITabProps> = ({ children }) => {
  return <TabContainer>{children}</TabContainer>;
};

export const TabStrip: React.FC<ITabStripProps> = ({ children, defaultActiveTabIndex = 0, renderRightChild, ...rest }) => {
  const [selectedTab, setSelectedTab] = useState(defaultActiveTabIndex);
  const { generateTestId } = useTestId(rest, 'TabStrip');

  useEffect(() => {
    setSelectedTab(defaultActiveTabIndex);
  }, [defaultActiveTabIndex]);

  const tabArray = React.Children.toArray(children)
    .filter((item) => React.isValidElement<ITabProps>(item))
    .map((item) => item as React.ReactElement<ITabProps>);

  const onTabClick = (e: React.MouseEvent<HTMLElement>, tab: React.ReactElement<ITabProps>) => {
    setSelectedTab(tabArray.findIndex((t) => t.props.title === tab.props.title));

    if (tab.props.onClick) {
      tab.props.onClick(e);
    }
  };

  return (
    <TabStripContainer {...generateTestId()}>
      <TabHeader {...generateTestId('Header')}>
        <TabChildren {...generateTestId('Children')}>
          {tabArray.map((tab, index) => (
            <TabItemHeader key={tab.props.title} onClick={(e) => onTabClick(e, tab)} isActive={index === selectedTab} {...generateTestId(`Header${index}`)}>
              <Text size={Size.Medium} {...generateTestId('Text')}>
                {tab.props.title}
              </Text>
            </TabItemHeader>
          ))}
        </TabChildren>
        <PullRight {...generateTestId('PullRight')}>{renderRightChild}</PullRight>
      </TabHeader>
      {tabArray[selectedTab]}
    </TabStripContainer>
  );
};
